import type { DatadogRum } from '@datadog/browser-rum';

import { scrubUrl } from './rumPrivacy';

type ReplayControls = Pick<
  DatadogRum,
  'startSessionReplayRecording' | 'stopSessionReplayRecording'
>;

export function hasSensitiveUrlData(url: string) {
  return scrubUrl(url) !== url;
}

function resolveUrl(url: string | URL | null | undefined) {
  if (url === null || url === undefined) return window.location.href;
  return new URL(url, window.location.href).href;
}

/**
 * Session replay writes the raw `window.location.href` into every full
 * snapshot (recording start and each new view). `beforeSend` never sees those
 * records, so recording is paused while the address bar holds data that
 * `scrubUrl` would redact, and resumed (with a fresh snapshot) once it is clean.
 * Requires `startSessionReplayRecordingManually: true`. Replay sampling is
 * decided once per session, so pausing and resuming never changes it.
 */
export function startReplayPrivacyGuard(rum: ReplayControls) {
  let isRecording = false;

  const pause = () => {
    if (!isRecording) return;
    rum.stopSessionReplayRecording();
    isRecording = false;
  };

  const resume = () => {
    if (isRecording) return;
    rum.startSessionReplayRecording();
    isRecording = true;
  };

  const sync = (url: string) => {
    if (hasSensitiveUrlData(url)) pause();
    else resume();
  };

  const patchHistoryMethod = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(
      this: History,
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) {
      const nextUrl = resolveUrl(url);
      if (hasSensitiveUrlData(nextUrl)) pause();
      original.call(this, data, unused, url);
      sync(window.location.href);
    };
    return () => {
      window.history[method] = original;
    };
  };

  // Must run before React Router's popstate handler, which starts the new view
  // (and its replay snapshot) synchronously. Two independent safeguards: the
  // DOM runs capture listeners at the target before non-capture ones, and the
  // guard is started from a module imported ahead of the router.
  const onPopState = () => sync(window.location.href);
  window.addEventListener('popstate', onPopState, { capture: true });

  const restorePushState = patchHistoryMethod('pushState');
  const restoreReplaceState = patchHistoryMethod('replaceState');

  sync(window.location.href);

  return () => {
    window.removeEventListener('popstate', onPopState, { capture: true });
    restorePushState();
    restoreReplaceState();
  };
}
